/**
 * Billing schedule utilities for deriving the next payment date.
 * Shared between components (SubscriptionCard, AllSubscriptionsTable) and unit tests.
 */

export type Frequency =
  | "weekly"
  | "fortnightly"
  | "monthly"
  | "every_2_months"
  | "quarterly"
  | "yearly"
  | "custom";

/**
 * Returns a new Date advanced by one billing cycle.
 */
export function addCycle(
  base: Date,
  frequency: Frequency,
  customFrequencyDays?: number,
  cycles: number = 1,
): Date {
  const d = new Date(base);
  switch (frequency) {
    case "weekly":
      d.setUTCDate(d.getUTCDate() + 7 * cycles);
      break;
    case "fortnightly":
      d.setUTCDate(d.getUTCDate() + 14 * cycles);
      break;
    case "monthly":
      d.setUTCMonth(d.getUTCMonth() + cycles);
      break;
    case "every_2_months":
      d.setUTCMonth(d.getUTCMonth() + 2 * cycles);
      break;
    case "quarterly":
      d.setUTCMonth(d.getUTCMonth() + 3 * cycles);
      break;
    case "yearly":
      d.setUTCFullYear(d.getUTCFullYear() + cycles);
      break;
    case "custom":
      d.setUTCDate(d.getUTCDate() + (customFrequencyDays || 30) * cycles);
      break;
    default:
      d.setUTCMonth(d.getUTCMonth() + cycles);
  }
  return d;
}

function normalizeDay(date: Date): Date {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

/**
 * Compute the next payment date based on the latest successful payment and frequency.
 * - If paidDates is non-empty, start from max(paidDates) + 1 cycle.
 * - Else if seedNextPayment is provided, start from that date.
 * - Leave unpaid past dates in the past so overdue cycles stay visible.
 */
export function computeNextPaymentDate(opts: {
  frequency: Frequency;
  customFrequencyDays?: number;
  paidDates?: Date[];
  seedNextPayment?: Date | null;
  today?: Date;
}): Date {
  const {
    frequency,
    customFrequencyDays,
    paidDates = [],
    seedNextPayment = null,
    today = new Date(),
  } = opts;

  const normToday = normalizeDay(today);

  // Latest paid date (ignore invalid dates)
  const validPaid = paidDates
    .filter((d) => d instanceof Date && !isNaN(d.getTime()))
    .sort((a, b) => b.getTime() - a.getTime());

  let next: Date;

  if (validPaid.length > 0) {
    next = addCycle(validPaid[0], frequency, customFrequencyDays);
  } else if (
    seedNextPayment &&
    seedNextPayment instanceof Date &&
    !isNaN(seedNextPayment.getTime())
  ) {
    next = new Date(seedNextPayment);
  } else {
    // Fallback: start from today and add one cycle
    next = addCycle(normToday, frequency, customFrequencyDays);
  }

  return next;
}

export function parseBillingCycle(billingCycle?: string | null): {
  frequency?: Frequency;
  customFrequencyDays?: number;
} {
  if (!billingCycle) {
    return {};
  }

  if (billingCycle.startsWith("custom_")) {
    return {
      frequency: "custom",
      customFrequencyDays: parseInt(billingCycle.split("_")[1], 10) || 30,
    };
  }

  return { frequency: billingCycle as Frequency };
}

export function resolveConsumedNextPaymentDate(opts: {
  billingCycle?: string | null;
  storedNextPaymentDate?: string | null;
  latestPaidDate?: string;
}): string | null {
  const storedNextPaymentDate = opts.storedNextPaymentDate ?? null;
  if (!storedNextPaymentDate) {
    return null;
  }

  if (!opts.latestPaidDate) {
    return storedNextPaymentDate;
  }

  const { frequency, customFrequencyDays } = parseBillingCycle(
    opts.billingCycle,
  );
  if (!frequency) {
    return storedNextPaymentDate;
  }

  const lastPaid = new Date(`${opts.latestPaidDate}T00:00:00.000Z`);
  const storedNext = new Date(`${storedNextPaymentDate}T00:00:00.000Z`);
  if (isNaN(lastPaid.getTime()) || isNaN(storedNext.getTime())) {
    return storedNextPaymentDate;
  }

  if (lastPaid.getTime() > storedNext.getTime()) {
    return storedNextPaymentDate;
  }

  const periodStart = getPeriodStart(
    storedNext,
    frequency,
    customFrequencyDays,
  );
  if (lastPaid.getTime() < periodStart.getTime()) {
    return storedNextPaymentDate;
  }

  return addCycle(lastPaid, frequency, customFrequencyDays)
    .toISOString()
    .split("T")[0];
}

export function deriveNextPaymentDate(opts: {
  billingCycle?: string | null;
  storedNextPaymentDate?: string | Date | null;
  paidDates?: Array<string | Date>;
  today?: Date;
}): Date | null {
  const { frequency, customFrequencyDays } = parseBillingCycle(
    opts.billingCycle,
  );
  const paidDates = (opts.paidDates ?? [])
    .map((date) => (date instanceof Date ? date : new Date(date)))
    .filter((date) => !isNaN(date.getTime()));
  const seedNextPayment = opts.storedNextPaymentDate
    ? opts.storedNextPaymentDate instanceof Date
      ? opts.storedNextPaymentDate
      : new Date(opts.storedNextPaymentDate)
    : null;

  if (!frequency) {
    return seedNextPayment && !isNaN(seedNextPayment.getTime())
      ? seedNextPayment
      : null;
  }

  return computeNextPaymentDate({
    frequency,
    customFrequencyDays,
    paidDates,
    seedNextPayment:
      seedNextPayment && !isNaN(seedNextPayment.getTime())
        ? seedNextPayment
        : null,
    today: opts.today,
  });
}

/**
 * Convenience: compute integer day delta from now/today to target date.
 * Positive = in future, 0 = today, negative = overdue days.
 */
export function daysUntil(target: Date, today: Date = new Date()): number {
  const t = normalizeDay(target).getTime();
  const n = normalizeDay(today).getTime();
  return Math.ceil((t - n) / (1000 * 60 * 60 * 24));
}

/**
 * Get the period start date from a period end date (e.g., next payment date) and billing cycle
 */
export function getPeriodStart(
  nextPaymentDate: Date,
  frequency: Frequency,
  customFrequencyDays?: number | null,
): Date {
  const periodStart = new Date(nextPaymentDate);

  switch (frequency) {
    case "weekly":
      periodStart.setUTCDate(periodStart.getUTCDate() - 7);
      break;
    case "fortnightly":
      periodStart.setUTCDate(periodStart.getUTCDate() - 14);
      break;
    case "monthly":
      periodStart.setUTCMonth(periodStart.getUTCMonth() - 1);
      break;
    case "every_2_months":
      periodStart.setUTCMonth(periodStart.getUTCMonth() - 2);
      break;
    case "quarterly":
      periodStart.setUTCMonth(periodStart.getUTCMonth() - 3);
      break;
    case "yearly":
      periodStart.setUTCFullYear(periodStart.getUTCFullYear() - 1);
      break;
    case "custom":
      const days = customFrequencyDays || 30;
      periodStart.setUTCDate(periodStart.getUTCDate() - days);
      break;
    default:
      periodStart.setUTCMonth(periodStart.getUTCMonth() - 1);
  }

  return periodStart;
}

/**
 * Check if a date falls within a billing period
 */
export function isInPeriod(
  targetDate: Date,
  periodStart: Date,
  frequency: Frequency,
  customFrequencyDays?: number | null,
): boolean {
  const periodEnd = addCycle(periodStart, frequency, customFrequencyDays);
  return (
    normalizeDay(targetDate) >= normalizeDay(periodStart) &&
    normalizeDay(targetDate) < normalizeDay(periodEnd)
  );
}

/**
 * Get the period end date from a period start date and billing cycle
 */
export function getPeriodEnd(
  periodStart: Date,
  frequency: Frequency,
  customFrequencyDays?: number | null,
): Date {
  return addCycle(periodStart, frequency, customFrequencyDays);
}
